import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import QRCode from '../../components/QRCode';
import SEOHead from '../../components/SEOHead';
import { useState } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Fluffy Pets',
    url: `${SITE_URL}/contact`,
    description:
      'Get in touch with Fluffy Pets for pet care inquiries, service bookings, or visit our San Francisco facility.',
    isPartOf: { '@type': 'WebSite', name: 'Fluffy Pets', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Fluffy Pets',
    url: SITE_URL,
    telephone: '+15551234567',
    email: 'hello@fluffypets.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Pet Care Lane',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94102',
      addressCountry: 'US',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Sunday'],
        opens: '10:00',
        closes: '17:00',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need an appointment?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'While walk-ins are welcome for some services, we highly recommend booking an appointment to ensure availability and minimize wait times.',
        },
      },
      {
        '@type': 'Question',
        name: "What should I bring for my pet's first visit?",
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Please bring your pet's vaccination records, any current medications, and information about their medical history. For boarding, bring their favorite toys and food.",
        },
      },
      {
        '@type': 'Question',
        name: 'Do you offer emergency services?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Our veterinary team provides 24/7 emergency care. Call us immediately if your pet needs urgent medical attention.',
        },
      },
      {
        '@type': 'Question',
        name: 'What payment methods do you accept?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We accept all major credit cards, debit cards, cash, and pet insurance. Payment is due at the time of service.',
        },
      },
    ],
  },
];

// ... existing code ...

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('loading');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      const response = await fetch('FORM_SUBMIT_URL', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact Fluffy Pets | Pet Care San Francisco - Get in Touch"
        description="Contact Fluffy Pets for pet grooming, boarding, veterinary care, and training inquiries. Visit us at 123 Pet Care Lane, San Francisco or call +1 (555) 123-4567."
        keywords="contact fluffy pets, pet care San Francisco contact, pet grooming appointment, veterinary San Francisco"
        canonical={`${SITE_URL}/contact`}
        schema={contactSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-tr from-pink-300/30 to-purple-300/30 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions about our services? Want to schedule a visit? We're here to help! Reach out to us and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Phone</h3>
              <p className="text-gray-600 mb-2">Give us a call</p>
              <a href="tel:+15551234567" className="text-purple-600 font-semibold hover:text-pink-600 transition-colors cursor-pointer">
                +1 (555) 123-4567
              </a>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Email</h3>
              <p className="text-gray-600 mb-2">Send us a message</p>
              <a href="mailto:hello@fluffypets.com" className="text-purple-600 font-semibold hover:text-pink-600 transition-colors cursor-pointer">
                hello@fluffypets.com
              </a>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto mb-6">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Location</h3>
              <p className="text-gray-600 mb-2">Visit our facility</p>
              <p className="text-purple-600 font-semibold">123 Pet Care Lane<br />San Francisco, CA 94102</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Fill out the form below and we'll get back to you within 24 hours. For urgent matters, please call us directly.
              </p>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="John Doe" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="john@example.com" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="+1 (555) 123-4567" />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject *</label>
                  <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all cursor-pointer">
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="grooming">Grooming Services</option>
                    <option value="boarding">Boarding Information</option>
                    <option value="veterinary">Veterinary Care</option>
                    <option value="training">Training Classes</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required
                    maxLength={500} rows={6}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us how we can help you..."></textarea>
                  <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>
                <button type="submit" disabled={submitStatus === 'loading'}
                  className="w-full px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {submitStatus === 'loading' ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                    Thank you! Your message has been sent successfully. We'll get back to you soon!
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    Oops! Something went wrong. Please try again or call us directly.
                  </div>
                )}
              </form>
            </div>
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Visit Our Facility
                </h3>
                <div className="w-full h-96 rounded-2xl overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0977736788845!2d-122.41941492346444!3d37.77492971267898!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085809c6c8f4459%3A0xb10ed6d9b5050fa5!2sSan%20Francisco%2C%20CA%2094102!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade" title="Fluffy Pets Location"></iframe>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-800">Business Hours</h3>
                <div className="space-y-4">
                  {[
                    { day: 'Monday - Friday', hours: '8:00 AM - 7:00 PM' },
                    { day: 'Saturday', hours: '9:00 AM - 6:00 PM' },
                    { day: 'Sunday', hours: '10:00 AM - 5:00 PM' }
                  ].map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center pb-4 border-b border-purple-200 last:border-0">
                      <span className="font-semibold text-gray-700">{schedule.day}</span>
                      <span className="text-purple-600 font-semibold">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-gray-600">
                  <i className="ri-information-line mr-2"></i>Emergency services available 24/7
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-12 text-center">
            <div className="mb-8">
              <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Quick Contact Access
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Scan this QR code to save our contact information on your mobile device or share it with others who need pet care services!
              </p>
            </div>
            <div className="flex justify-center mb-6">
              <QRCode url={`${window.location.origin}/contact`} size={220} showDownload={true} />
            </div>
            <div className="mt-6">
              <a href={`https://wa.me/97455309661?text=${encodeURIComponent('Check out Fluffy Pets! Visit our website: ' + window.location.origin + '/contact')}`}
                target="_blank" rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-2xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
                <i className="ri-whatsapp-line text-2xl"></i>
                Share via WhatsApp
              </a>
              <p className="text-sm text-gray-500 mt-3">Click to share our contact info on WhatsApp</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">Quick answers to common questions</p>
          </div>
          <div className="space-y-6">
            {[
              { question: 'Do I need an appointment?', answer: 'While walk-ins are welcome for some services, we highly recommend booking an appointment to ensure availability and minimize wait times.' },
              { question: "What should I bring for my pet first visit?", answer: "Please bring your pet vaccination records, any current medications, and information about their medical history. For boarding, bring their favorite toys and food." },
              { question: 'Do you offer emergency services?', answer: 'Yes! Our veterinary team provides 24/7 emergency care. Call us immediately if your pet needs urgent medical attention.' },
              { question: 'What payment methods do you accept?', answer: 'We accept all major credit cards, debit cards, cash, and pet insurance. Payment is due at the time of service.' }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-3 text-gray-800">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
